import { COURSE } from './Mission.js';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

const headingLabel = (radians) => {
  const degrees = ((-radians * 180 / Math.PI) % 360 + 360) % 360;
  return Math.round(degrees).toString().padStart(3, '0');
};

export class GameUI {
  constructor(root, callbacks = {}) {
    this.root = root;
    this.callbacks = callbacks;
    this.toastTimer = null;
    this.lastScore = 0;
    this.render();
    this.cache();
    this.bind();
  }

  render() {
    const gateTicks = COURSE.map((_, index) => `<i data-gate="${index}"></i>`).join('');
    this.root.innerHTML = `
      <div class="boot"><div class="boot__mark"><span class="boot__ring"></span><span>Preparing airspace</span></div></div>
      <div class="cinema-bars" aria-hidden="true"><i></i><i></i></div>
      <header class="topbar hud" data-hud>
        <div class="brand"><span class="brand__wing"></span><b>SKYLINE</b><em>FALCON RUN</em></div>
        <div class="mission-clock"><span>MISSION TIME</span><b data-time>00:00</b></div>
        <button class="icon-button" data-pause aria-label="Pause game"><span></span><span></span></button>
      </header>

      <section class="flight-data hud" data-hud aria-label="Flight data">
        <div class="instrument"><span>AIRSPEED</span><strong data-speed>000</strong><small>KTS</small></div>
        <div class="instrument"><span>ALTITUDE</span><strong data-altitude>0000</strong><small>FT AGL</small></div>
        <div class="instrument instrument--vertical"><span>VERTICAL</span><strong data-vertical>+000</strong><small>FT / MIN</small></div>
        <div class="throttle">
          <span>THR</span><div class="throttle__track"><i data-throttle-bar></i></div><b data-throttle>08</b>
        </div>
      </section>

      <section class="score-panel hud" data-hud>
        <span>SCORE</span><strong data-score>000000</strong>
        <div class="gate-progress">${gateTicks}</div>
      </section>

      <div class="attitude hud" data-hud aria-hidden="true">
        <div class="attitude__horizon" data-horizon>
          <i class="pitch pitch--up"><span>10</span></i>
          <i class="pitch pitch--center"></i>
          <i class="pitch pitch--down"><span>10</span></i>
        </div>
        <div class="reticle"><i></i><b></b><i></i></div>
        <div class="flight-path" data-flight-path></div>
      </div>

      <div class="compass hud" data-hud>
        <span>HDG</span><b data-heading>000</b><div class="compass__caret"></div>
      </div>

      <section class="objective hud" data-hud>
        <span class="objective__eyebrow" data-objective-label>TAKEOFF</span>
        <strong data-objective>Advance throttle and rotate</strong>
        <em data-distance>RUNWAY 09</em>
      </section>

      <section class="radar hud" data-hud>
        <div class="radar__label"><span>TACTICAL</span><b>ROUTE</b></div>
        <canvas data-radar width="320" height="220"></canvas>
      </section>

      <div class="warning warning--stall" data-stall><span>STALL</span><small>LOWER NOSE · ADD POWER</small></div>
      <div class="toast" data-toast><span data-toast-title>Gate clear</span><b data-toast-points>+800</b></div>

      <div class="touch-controls hud" data-hud>
        <div class="joystick" data-joystick><span>ROLL / PITCH</span><i data-joystick-knob></i></div>
        <div class="touch-actions">
          <button data-hold="throttle:1"><span>+</span>THR</button>
          <button data-camera><span>◉</span>VIEW</button>
          <button data-hold="throttle:-1"><span>−</span>THR</button>
        </div>
      </div>

      <section class="intro panel" data-intro>
        <div class="intro__wash"></div>
        <div class="intro__content">
          <span class="kicker"><i></i> ALPINE COAST · 06:42 LOCAL</span>
          <h1>SKY<span>LINE</span></h1>
          <p>One aircraft. Ten gates. A mountain pass between you and runway 27.</p>
          <div class="mission-spec">
            <div><span>COURSE</span><b>14.8 KM</b></div>
            <div><span>AIRCRAFT</span><b>FALCON S9</b></div>
            <div><span>WEATHER</span><b>VFR · CALM</b></div>
          </div>
          <button class="primary-button" data-start><span>START ENGINE</span><i>↗</i></button>
          <div class="controls-legend">
            <span><kbd>W S</kbd> PITCH</span><span><kbd>A D</kbd> ROLL</span>
            <span><kbd>SHIFT CTRL</kbd> THROTTLE</span><span><kbd>C</kbd> CAMERA</span>
          </div>
        </div>
        <div class="intro__side"><span>SKYLINE AIR RACING</span><i></i><b>01</b></div>
      </section>

      <section class="pause-panel panel" data-pause-panel>
        <span class="kicker">FLIGHT SUSPENDED</span><h2>Paused</h2>
        <button class="primary-button" data-resume><span>RESUME FLIGHT</span><i>↗</i></button>
        <button class="text-button" data-restart>Restart mission</button>
      </section>

      <section class="result-panel panel" data-result>
        <span class="kicker" data-result-kicker>MISSION COMPLETE</span>
        <h2 data-result-title>Runway secured.</h2>
        <p data-result-copy>A clean flight through the Alpine Corridor.</p>
        <div class="result-score"><span>FINAL SCORE</span><b data-final-score>000000</b></div>
        <div class="result-stats">
          <div><span>TIME</span><b data-final-time>00:00</b></div>
          <div><span>GATES</span><b data-final-gates>10 / 10</b></div>
          <div><span>NEAR MISSES</span><b data-final-near>0</b></div>
        </div>
        <button class="primary-button" data-fly-again><span>FLY AGAIN</span><i>↗</i></button>
      </section>

      <div class="renderer-chip" data-renderer>INITIALIZING</div>
      <div class="damage-flash" data-damage></div>
    `;
  }

  cache() {
    const find = (selector) => this.root.querySelector(selector);
    this.elements = {
      boot: find('.boot'), intro: find('[data-intro]'), pause: find('[data-pause-panel]'),
      result: find('[data-result]'), renderer: find('[data-renderer]'),
      speed: find('[data-speed]'), altitude: find('[data-altitude]'), vertical: find('[data-vertical]'),
      throttle: find('[data-throttle]'), throttleBar: find('[data-throttle-bar]'),
      score: find('[data-score]'), time: find('[data-time]'), heading: find('[data-heading]'),
      horizon: find('[data-horizon]'), flightPath: find('[data-flight-path]'),
      objectiveLabel: find('[data-objective-label]'), objective: find('[data-objective]'),
      distance: find('[data-distance]'), stall: find('[data-stall]'),
      toast: find('[data-toast]'), toastTitle: find('[data-toast-title]'), toastPoints: find('[data-toast-points]'),
      radar: find('[data-radar]'), damage: find('[data-damage]'),
      finalScore: find('[data-final-score]'), finalTime: find('[data-final-time]'),
      finalGates: find('[data-final-gates]'), finalNear: find('[data-final-near]'),
      resultKicker: find('[data-result-kicker]'), resultTitle: find('[data-result-title]'),
      resultCopy: find('[data-result-copy]'), gates: [...this.root.querySelectorAll('[data-gate]')],
      hud: [...this.root.querySelectorAll('[data-hud]')]
    };
  }

  bind() {
    this.root.querySelector('[data-start]').addEventListener('click', () => this.callbacks.onStart?.());
    this.root.querySelector('[data-resume]').addEventListener('click', () => this.callbacks.onPause?.());
    this.root.querySelector('[data-restart]').addEventListener('click', () => this.callbacks.onRestart?.());
    this.root.querySelector('[data-fly-again]').addEventListener('click', () => this.callbacks.onRestart?.());
  }

  ready(backend) {
    this.elements.renderer.textContent = backend;
    requestAnimationFrame(() => this.elements.boot.classList.add('ready'));
  }

  start() {
    this.root.classList.add('game-running');
    this.elements.intro.classList.add('is-hidden');
    this.elements.result.classList.remove('is-visible');
    this.elements.pause.classList.remove('is-visible');
    this.elements.hud.forEach((element) => element.classList.add('is-visible'));
    this.lastScore = 0;
  }

  setPaused(paused) {
    this.elements.pause.classList.toggle('is-visible', paused);
  }

  toast(title, points, tone = 'amber') {
    this.elements.toastTitle.textContent = title;
    this.elements.toastPoints.textContent = points > 0 ? `+${points}` : `${points}`;
    this.elements.toast.dataset.tone = tone;
    this.elements.toast.classList.remove('is-visible');
    void this.elements.toast.offsetWidth;
    this.elements.toast.classList.add('is-visible');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.elements.toast.classList.remove('is-visible'), 1800);
  }

  flashDamage() {
    this.elements.damage.classList.remove('is-visible');
    void this.elements.damage.offsetWidth;
    this.elements.damage.classList.add('is-visible');
  }

  update(flight, mission, surface) {
    const knots = Math.round(flight.speed * 1.94384);
    const altitude = Math.max(0, Math.round((flight.position.y - surface.groundHeight) * 3.28084));
    const vertical = Math.round(flight.verticalSpeed * 196.85);
    this.elements.speed.textContent = knots.toString().padStart(3, '0');
    this.elements.altitude.textContent = altitude.toString().padStart(4, '0');
    this.elements.vertical.textContent = `${vertical >= 0 ? '+' : '−'}${Math.abs(vertical).toString().padStart(3, '0')}`;
    this.elements.vertical.classList.toggle('is-negative', vertical < -500);
    this.elements.throttle.textContent = Math.round(flight.throttle * 100).toString().padStart(2, '0');
    this.elements.throttleBar.style.transform = `scaleY(${flight.throttle})`;
    this.elements.score.textContent = Math.round(mission.score).toString().padStart(6, '0');
    this.elements.score.classList.toggle('is-popping', mission.score !== this.lastScore);
    if (mission.score !== this.lastScore) {
      window.setTimeout(() => this.elements.score.classList.remove('is-popping'), 260);
      this.lastScore = mission.score;
    }
    this.elements.time.textContent = formatTime(mission.elapsed);
    this.elements.heading.textContent = headingLabel(flight.heading);
    this.elements.horizon.style.transform = `translateY(${flight.pitch * 145}px) rotate(${-flight.roll}rad)`;
    this.elements.flightPath.style.transform = `translate(${Math.sin(flight.roll) * 28}px, ${-flight.verticalSpeed * 1.2}px)`;
    this.elements.stall.classList.toggle('is-visible', flight.stall);
    this.elements.gates.forEach((gate, index) => gate.classList.toggle('is-complete', index < mission.rings));
    this.updateObjective(flight, mission);
    this.drawRadar(flight, mission);
  }

  updateObjective(flight, mission) {
    if (mission.phase === 'takeoff') {
      this.elements.objectiveLabel.textContent = 'TAKEOFF';
      this.elements.objective.textContent = flight.speed < 48 ? 'Full throttle · Hold centerline' : 'Rotate · Pull back gently';
      this.elements.distance.textContent = 'RUNWAY 09';
      return;
    }
    if (mission.phase === 'course') {
      const ring = COURSE[mission.activeRing];
      const distance = Math.hypot(
        flight.position.x - ring.x,
        flight.position.y - ring.y,
        flight.position.z - ring.z
      );
      this.elements.objectiveLabel.textContent = `GATE ${mission.activeRing + 1} / ${COURSE.length}`;
      this.elements.objective.textContent = ring.label;
      this.elements.distance.textContent = `${Math.round(distance)} M`;
      return;
    }
    if (mission.phase === 'approach') {
      const distance = Math.max(0, Math.abs(flight.position.z + 12600));
      this.elements.objectiveLabel.textContent = 'FINAL APPROACH';
      this.elements.objective.textContent = 'Runway 27 · Gear down';
      this.elements.distance.textContent = `${Math.round(distance)} M TO THRESHOLD`;
    }
  }

  drawRadar(flight, mission) {
    const canvas = this.elements.radar;
    const context = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const mapPoint = (x, z) => ({
      x: width / 2 + x * 0.17,
      y: 10 + ((650 - z) / 15650) * (height - 20)
    });
    context.clearRect(0, 0, width, height);
    context.strokeStyle = 'rgba(255,255,255,.08)';
    context.lineWidth = 1;
    for (let line = 1; line < 4; line += 1) {
      context.beginPath();
      context.moveTo(0, line * height / 4);
      context.lineTo(width, line * height / 4);
      context.stroke();
    }
    context.beginPath();
    COURSE.forEach((ring, index) => {
      const point = mapPoint(ring.x, ring.z);
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    });
    context.strokeStyle = 'rgba(255,190,90,.36)';
    context.lineWidth = 2;
    context.stroke();
    COURSE.forEach((ring, index) => {
      const point = mapPoint(ring.x, ring.z);
      context.beginPath();
      context.arc(point.x, point.y, index === mission.activeRing ? 6 : 3.2, 0, Math.PI * 2);
      context.fillStyle = index < mission.rings ? '#72e0c9' : index === mission.activeRing ? '#ffba58' : 'rgba(255,255,255,.3)';
      context.fill();
    });
    const plane = mapPoint(flight.position.x, flight.position.z);
    context.save();
    context.translate(plane.x, plane.y);
    context.rotate(-flight.heading);
    context.beginPath();
    context.moveTo(0, -8);
    context.lineTo(5, 7);
    context.lineTo(0, 4);
    context.lineTo(-5, 7);
    context.closePath();
    context.fillStyle = '#ffffff';
    context.shadowColor = '#ffffff';
    context.shadowBlur = 8;
    context.fill();
    context.restore();
  }

  showResult(mission, success) {
    this.elements.resultKicker.textContent = success ? 'MISSION COMPLETE' : 'AIRCRAFT LOST';
    this.elements.resultTitle.textContent = success ? 'Runway secured.' : 'The mountain keeps score.';
    this.elements.resultCopy.textContent = success
      ? 'A clean flight through the Alpine Corridor.'
      : 'Reset, respect the airspeed, and fly the line again.';
    this.elements.finalScore.textContent = mission.score.toString().padStart(6, '0');
    this.elements.finalTime.textContent = formatTime(mission.elapsed);
    this.elements.finalGates.textContent = `${mission.rings} / ${COURSE.length}`;
    this.elements.finalNear.textContent = mission.nearMisses;
    this.elements.result.dataset.result = success ? 'success' : 'crash';
    window.setTimeout(() => this.elements.result.classList.add('is-visible'), success ? 850 : 650);
  }
}
